import {
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TaskComponent } from '../task/task.component';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CalendarTasksGroup,
  CommonUtils,
  HourSelectionEvent,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { CalendarSection } from '../calendar-section';
import { Telework } from '../../telework';
import { TeleworkService } from '../../telework.service';

export type TaskObject = {
  task: CalendarTask;
  style: any;
};

@Component({
  selector: 'infl-calendar-section',
  standalone: true,
  imports: [CommonModule, TaskComponent],
  templateUrl: './calendar-section.component.html',
  styleUrl: './calendar-section.component.scss',
})
export class CalendarSectionComponent {
  public section = input.required<CalendarSection>();
  public resources = input<CalendarTaskHeaderGroup[]>();
  public height = input.required<number>();
  public taskHeight = input.required<number>();
  public canDrag = input.required<boolean>();
  public canResize = input.required<boolean>();
  public canSelect = input.required<boolean>();
  public outsideDraggingTask = input<CalendarTask>();
  public dragByOutside = input<CalendarDropTask<any>>();
  public resource = input<string>(null);
  public teleworks = input<Telework[]>(null);

  public taskUpdate = output<CalendarTask>();
  public dropOutside = output();
  public dragTask = output<CalendarTask>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public hourSelection = output<WeekSelectionEvent>();
  public hourPreselection = output<WeekSelectionEvent>();
  public lastSectionHighlighted = output<DragEvent>();
  public taskCreation = output<{
    date: Date;
    group: { header: string; group: string };
  }>();

  private draggingTask: { task: CalendarTask; style: any };
  private zones: HTMLElement[];
  private resizeStart: number;
  private taskWidth: number;
  private taskLeft: number;
  private resizingTask: {
    border: 'left' | 'right';
    task: CalendarTask;
    element: any;
  };

  public readonly teleworkService = inject(TeleworkService);

  public tasks = signal<TaskObject[]>([]);
  public hasTelework = signal(false);

  constructor() {
    effect(() => {
      if (this.section()) {
        this.tasks.set([]);
        setTimeout(() => {
          this.tasks.set(this.generateTasks());
        }, 10);
      }
    });
    effect(() => {
      if (this.teleworks()) {
        const telework = this.teleworks().find((t) => {
          return (
            t.resourceId === this.resource() &&
            this.section().startMorning.format('DD/MM/YYYY') === t.date
          );
        });
        this.hasTelework.set(!!telework);
      }
    });
  }

  private withoutOverflowingTasks(
    overflowingTasks: CalendarTask[],
  ): CalendarTask[] {
    const tasks = [];
    for (const task of this.section().section1) {
      if (!overflowingTasks.find((t) => t.id === task.id)) {
        tasks.push(task);
      }
    }
    return tasks;
  }

  private getOverflowingTasks(): CalendarTask[] {
    return this.section().section1.filter(
      (task) =>
        task.dateStart.isBefore(this.section().endMorning.clone()) &&
        task.dateEnd.isAfter(this.section().startAfternoon.clone()),
    );
  }

  private generateTasks(): TaskObject[] {
    const tasks: TaskObject[] = [];
    const width = document.getElementById('sections').clientWidth + 1;
    let overflowingTasks = [];
    if (this.section().nbSection === 2) {
      overflowingTasks = this.getOverflowingTasks();
      for (let i = 0; i < overflowingTasks.length; i++) {
        const taskObject: TaskObject = {
          task: overflowingTasks[i],
          style: {
            top: `${i * (this.taskHeight() + 5)}px`,
            width: `${width}px`,
            height: `${this.taskHeight()}px`,
          },
        };
        tasks.push(taskObject);
      }
    }
    const notOverflowingTasks = this.withoutOverflowingTasks(overflowingTasks);
    for (let i = 0; i < notOverflowingTasks.length; i++) {
      const taskObject: TaskObject = {
        task: notOverflowingTasks[i],
        style: {
          top: `${(i + overflowingTasks.length) * (this.taskHeight() + 5)}px`,
          width: `${this.section().nbSection === 1 ? width : width / 2}px`,
          height: `${this.taskHeight()}px`,
        },
      };
      tasks.push(taskObject);
    }
    for (let i = 0; i < this.section().section2.length; i++) {
      const taskObject: TaskObject = {
        task: this.section().section2[i],
        style: {
          top: `${(i + overflowingTasks.length) * (this.taskHeight() + 5)}px`,
          left: `${width / 2}px`,
          width: `${width / 2}px`,
          height: `${this.taskHeight()}px`,
        },
      };
      tasks.push(taskObject);
    }
    return tasks;
  }

  public stop(event: MouseEvent) {
    this.stopSelection(event);
    this.stopResize();
  }

  public startResize(resize: {
    event: MouseEvent;
    task: CalendarTask;
    direction: 'left' | 'right';
  }) {
    if (this.canResize()) {
      const target = (<any>resize.event.target).closest('.task');
      target.style.opacity = '0.3';
      this.resizingTask = {
        element: target,
        task: resize.task,
        border: resize.direction,
      };
      this.resizeStart = resize.event.x;
      this.taskWidth = target.clientWidth;
      this.taskLeft = target.offsetLeft;
    }
  }

  public onResize(event: MouseEvent) {
    if (!this.resizingTask) {
      return;
    }
    if (this.resizingTask.border === 'right') {
      this.resizingTask.element.style.width = `${this.taskWidth + (event.x - this.resizeStart)}px`;
      return;
    }
    if (this.resizingTask.border === 'left') {
      const taskStyle = this.resizingTask.element.style;
      taskStyle.left = `${this.taskLeft + (event.x - this.resizeStart)}px`;
      taskStyle.width = `${this.taskWidth - (event.x - this.resizeStart)}px`;
    }
  }

  private stopResize() {
    if (this.resizingTask) {
      this.resizingTask.element.style.opacity = '1';
      const width = document.getElementById('sections').clientWidth;
      const task = this.resizingTask.task;
      const taskWidth = Number(
        this.resizingTask.element.style.width.replace('px', ''),
      );
      if (taskWidth === 0) {
        setTimeout(() => this.clearResizing(), 10);
        return;
      }
      if (taskWidth > width / 2) {
        task.dateStart = this.section().startMorning.clone();
        task.dateEnd = this.section().endAfternoon.clone();
      }
      if (this.resizingTask.border === 'right' && taskWidth < width / 2) {
        task.dateStart = this.section().startMorning.clone();
        task.dateEnd = this.section().endMorning.clone();
      }
      const taskLeft = Number(
        this.resizingTask.element.style.left.replace('px', ''),
      );
      if (this.resizingTask.border === 'left' && taskLeft > width / 2) {
        task.dateStart = this.section().startAfternoon.clone();
        task.dateEnd = this.section().endAfternoon.clone();
      }
      this.taskUpdate.emit(task);
      setTimeout(() => this.clearResizing(), 10);
    }
  }

  private clearResizing() {
    this.resizingTask = null;
    this.resizeStart = 0;
    this.taskWidth = null;
    this.taskLeft = null;
  }

  onDragStart(task: { task: CalendarTask; style: any }) {
    if (this.canDrag()) {
      this.draggingTask = task;
      this.dragTask.emit(this.draggingTask.task);
      setTimeout(() => {
        this.managePointerEvents('none');
      }, 100);
    }
  }

  public managePointerEvents(value: 'none' | 'auto') {
    const sections = document.getElementsByClassName('sections');
    for (let i = 0; i < sections.length; i++) {
      const tasks = sections[i].getElementsByClassName('task');
      for (let j = 0; j < tasks.length; j++) {
        if (!tasks[j].classList.contains('enable')) {
          (tasks[j] as HTMLElement).style.pointerEvents = value;
        }
      }
    }
  }

  onDrop(sectionNumber: number) {
    const task = this.draggingTask
      ? this.draggingTask.task
      : this.outsideDraggingTask();
    if (this.outsideDraggingTask()) {
      this.managePointerEvents('auto');
    }
    if (task) {
      const previousDuration = task.getDuration();
      if (
        this.section().nbSection === 1 ||
        (this.section().nbSection === 2 && sectionNumber === 1)
      ) {
        task.dateStart = this.section().startMorning.clone();
      }
      if (this.section().nbSection === 2 && sectionNumber === 2) {
        task.dateStart = this.section().startAfternoon.clone();
      }
      task.dateEnd = task.dateStart
        .clone()
        .add(
          CommonUtils.convertStrToTimeInMillis(previousDuration),
          'milliseconds',
        );
      if (
        task.group.header !== this.section().group.id ||
        task.group.group !== this.section().resource.id
      ) {
        this.changeTaskGroup(
          task,
          this.section().group.id,
          this.section().resource.id,
        );
      }
      task.group = {
        header: this.section().group.id,
        group: this.section().resource.id,
      };
      this.taskUpdate.emit(task);
      this.draggingTask = null;
      this.dropOutside.emit();
      return;
    }
    if (this.dragByOutside()) {
      this.taskCreation.emit({
        date:
          sectionNumber === 1
            ? this.section().startMorning.clone().toDate()
            : this.section().startAfternoon.clone().toDate(),
        group: {
          header: this.section().group.id,
          group: this.section().resource.id,
        },
      });
    }
  }

  private changeTaskGroup(
    task: CalendarTask,
    headerKey: string,
    groupKey: string,
  ) {
    const group = this.getGroup(headerKey, groupKey);
    if (group) {
      group.addTask(task);
    }
    const oldGroup = this.getGroup(task.group.header, task.group.group);
    if (oldGroup) {
      oldGroup.removeTask(task);
    }
  }

  private getGroup(headerKey, groupKey): CalendarTasksGroup {
    const header = this.resources().find((res) => res.id === headerKey);
    if (header) {
      return header.tasksGroups.find((tg) => tg.id === groupKey);
    }
    return null;
  }

  selectZone(event: MouseEvent) {
    if (this.canSelect()) {
      event.stopPropagation();
      event.preventDefault();
      const zone = event.target as HTMLElement;
      zone.classList.add('selected');
      this.zones = [zone];
    }
  }

  public deselect() {
    if (!this.zones) {
      return;
    }
    this.zones.forEach((zone) => {
      zone.classList.remove('selected');
    });
    this.zones = null;
  }

  private stopSelection(event: MouseEvent) {
    if (this.canSelect() && this.zones) {
      let start = {
        hours: this.section().startMorning.get('hour'),
        minutes: this.section().startMorning.get('minute'),
      };
      let end = {
        hours: this.section().endAfternoon.get('hour'),
        minutes: this.section().endAfternoon.get('minute'),
      };
      if (
        this.section().nbSection === 2 &&
        this.zones.length === 1 &&
        this.zones[0].classList.contains('section1')
      ) {
        end = {
          hours: this.section().endMorning.get('hour'),
          minutes: this.section().endMorning.get('minute'),
        };
      }
      if (
        this.section().nbSection === 2 &&
        this.zones.length === 1 &&
        this.zones[0].classList.contains('section2')
      ) {
        start = {
          hours: this.section().startAfternoon.get('hour'),
          minutes: this.section().startAfternoon.get('minute'),
        };
      }
      const selection = {
        start: start,
        end: end,
        resource: this.section().resource,
      };
      const hourSelection = new HourSelectionEvent(selection, event);
      this.hourSelection.emit(
        new WeekSelectionEvent(
          this.section().startMorning.toDate(),
          hourSelection,
        ),
      );
    }
  }

  overZone(event: MouseEvent, sectionNumber: number) {
    if (this.canSelect() && this.zones) {
      const zone = event.target as HTMLElement;
      zone.classList.add('selected');
      this.zones.push(zone);
      event.stopPropagation();
      event.preventDefault();
    } else {
      let start = {
        hours: this.section().startMorning.get('hour'),
        minutes: this.section().startMorning.get('minute'),
      };
      let end = {
        hours: this.section().endAfternoon.get('hour'),
        minutes: this.section().endAfternoon.get('minute'),
      };
      if (this.section().nbSection === 2 && sectionNumber === 1) {
        end = {
          hours: this.section().endMorning.get('hour'),
          minutes: this.section().endMorning.get('minute'),
        };
      }
      if (this.section().nbSection === 2 && sectionNumber === 2) {
        start = {
          hours: this.section().startAfternoon.get('hour'),
          minutes: this.section().startAfternoon.get('minute'),
        };
      }
      const selection = {
        start: start,
        end: end,
        resource: this.section().resource,
      };
      this.hourPreselection.emit(
        new WeekSelectionEvent(
          this.section().startMorning.toDate(),
          new HourSelectionEvent(selection, event),
        ),
      );
    }
  }

  onDragEnd() {
    setTimeout(() => {
      this.managePointerEvents('auto');
    }, 100);
  }

  onTaskClick(event: CalendarClickEvent) {
    if (this.resizingTask) {
      return;
    }
    this.taskClick.emit(event);
  }

  protected readonly CommonUtils = CommonUtils;
}
